import { Close } from '@mui/icons-material';
import { Backdrop, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Theme, useMediaQuery, useTheme } from '@mui/material';
import React, { MouseEvent, type JSX } from 'react';
import { BaseDialogResult, bindDialog, BindDialogReturn, DialogProps, DialogState, useDialogState } from '../../hooks/use-dialog-state/UseDialogState';
import { SxProps } from "@mui/system";

export type ConfirmationDialogInitValues = {
    dialogTitle: string;
    confirmationText?: string | null;
    showConfirmationText?: boolean;
    confirmationBodyComponent?: JSX.Element,
    showYesButton?: boolean;
    yesButtonText?: string | null;
    yesButtonDisabled?: boolean;
    showNoButton?: boolean;
    noButtonText?: string | null;
    noButtonDisabled?: boolean;
    showCloseButton?: boolean;
    contentSx?: SxProps<Theme>;
};

export type ConfirmationDialogResultReason = "yes" | "no" | "close";

export type ConfirmationDialogResult = BaseDialogResult<ConfirmationDialogResultReason>;

export type ConfirmationDialogProps = DialogProps<ConfirmationDialogInitValues, ConfirmationDialogResult>;

export function ConfirmationDialog({
    open,
    onClose,
    values,
    ...props
}: ConfirmationDialogProps): JSX.Element {

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

    const handleClose = (event: MouseEvent<HTMLButtonElement>) => {

        const btn = event.currentTarget as HTMLButtonElement;
        onClose && onClose({
            reason: btn.name as ConfirmationDialogResultReason
        });
    };

    return (
        <Backdrop sx={{
            zIndex: theme.zIndex.drawer + 1,
            color: '#fff',
        }} open={open}>

            <Dialog
                fullScreen={!isDesktop}
                fullWidth={true}
                maxWidth="sm"
                open={open}
                onClose={(event, reason) => {
                    if (reason !== "escapeKeyDown") {
                        handleClose(event as MouseEvent<HTMLButtonElement>);
                    }
                }}
                aria-labelledby="confirmation-dialog-title"
                aria-describedby="confirmation-dialog-description"
                {...props}>
                {values?.dialogTitle &&
                    <DialogTitle id="confirmation-dialog-title">
                        {values.dialogTitle}
                    </DialogTitle>
                }
                {values?.showCloseButton &&

                    <IconButton
                        name="close"
                        aria-label="close"
                        sx={{
                            position: 'absolute',
                            right: theme.spacing(1),
                            top: theme.spacing(1)
                        }}
                        onClick={handleClose}
                        size="large">
                        <Close />
                    </IconButton>

                }

                <DialogContent sx={values?.contentSx}>
                    <DialogContentText id="confirmation-dialog-description" component='div'>
                        {values?.showConfirmationText && values?.confirmationText}
                    </DialogContentText>

                    {/* this replaces html-with-bindings */}
                    {values?.confirmationBodyComponent}

                </DialogContent>

                <DialogActions>
                    {
                        values?.showNoButton &&
                        <Button name='no'
                            data-testid="no"
                            onClick={handleClose}
                            disabled={values.noButtonDisabled}
                        >
                            {values.noButtonText}
                        </Button>
                    }

                    {
                        values?.showYesButton &&
                        <Button
                            name='yes'
                            data-testid="yes"
                            onClick={handleClose}
                            color="secondary"
                            disabled={values.yesButtonDisabled}
                        >
                            {values.yesButtonText}
                        </Button>
                    }

                </DialogActions>
            </Dialog>

        </Backdrop>
    );
}

export type ConfirmationDialogState = DialogState<ConfirmationDialogInitValues, ConfirmationDialogResult>;
export type BindConfirmationDialogReturn = BindDialogReturn<ConfirmationDialogInitValues, ConfirmationDialogResult>;

export function useConfirmationDialogState(): ConfirmationDialogState {
    return useDialogState();
}

export function bindConfirmationDialog(props: ConfirmationDialogState): BindConfirmationDialogReturn {
    return bindDialog(props);
}
